import "./ToDoList.scss";
import ActionBar from "../../components/ActionBar/ActionBar";
import ActionBarButton from "../../components/ActionBar/ActionBarButton";
import FlowTextInput from "../../components/FlowTextInput/FlowTextInput";
import EmptyStatePlaceholder from "../../components/EmptyStatePlaceholder/EmptyStatePlaceholder";
import Modal from "../../components/Modal/Modal";
import ToolTip from "../../components/ToolTip/ToolTip";
import RemoveTodoList from "./Modals/RemoveTodoList/RemoveTodoList";
import RemoveCompletedItems from "./Modals/RemoveCompletedItems/RemoveCompletedItems";
import AddTodoList from "./Modals/AddTodoList/AddTodoList";
import EditTodoList from "./Modals/EditTodoList/EditTodoList";
import EditItemDescription from "./Modals/EditItemDescription/EditItemDescription";
import DownloadList from "./Modals/DownloadList/DownloadList";
import SpacerModal from "./Modals/SpacerModal/SpacerModal";
import MoveItemModal from "./Modals/MoveItemModal/MoveItemModal";
import KeepItemDeadline from "./Modals/KeepItemDeadline/KeepItemDeadline";
import { withAlpha } from "../../utils/colors";
import { getAddToTop, insertUnderSpacer } from "../../utils/todoListItems";
import {
  applySpacerDeadline,
  applyMoveDeadlines,
  applyNewItemDeadline,
  removeItemDeadline,
} from "../../utils/spacerDeadlines";
import { parsePastedList } from "../../utils/pastedList";
import Deadline from "../Tasks/Modals/Deadline/Deadline";
import { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import { useUserRealtime, useResync } from "../../hooks/useRealtime";
import { getTextPreview } from "../../utils/textPreview";
import { jsPDF } from "jspdf";
import { loadExo2Font } from "../../utils/pdfFonts";
import {
  getPersonalTodoLists,
  getProjectTodoLists,
  createTodoList,
  updateTodoList,
  deleteTodoList,
  reorderTodoLists,
} from "../../api/todoLists";
import { getLinkedCounts } from "../../api/tasks/getLinkedCounts";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";

// Helper function to determine deadline status
const getDeadlineStatus = (deadline) => {
  if (!deadline) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deadlineDate = new Date(deadline);
  deadlineDate.setHours(0, 0, 0, 0);

  if (deadlineDate < today) return "overdue";
  if (deadlineDate.getTime() === today.getTime()) return "due-today";
  return null;
};

// Sortable Todo List Item Component (left column)
function SortableTodoListItem({
  todo,
  isSelected,
  onClick,
  onEdit,
  onDelete,
  linkedCount = 0,
  isOverForItemDrop = false,
  disabled = false,
}) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: todo._id, disabled });

  const { setNodeRef: setDroppableRef, isOver: isOverDroppable } = useDroppable(
    {
      id: `list-drop-${todo._id}`,
    },
  );

  const isChecklist = todo.type === "checklist";

  const style = {
    transform: disabled ? undefined : CSS.Transform.toString(transform),
    transition: isDragging ? "none" : transition,
    opacity: isDragging ? 0.5 : 1,
    borderLeftColor: todo.color,
    backgroundColor:
      isOverForItemDrop || isOverDroppable
        ? "rgba(59, 130, 246, 0.15)"
        : undefined,
  };

  // Combine refs for both sortable and droppable
  const combinedRef = (element) => {
    setNodeRef(element);
    setDroppableRef(element);
  };

  return (
    <div
      ref={combinedRef}
      style={style}
      className={`todo-item ${isSelected ? "selected" : ""} ${isChecklist ? "checklist" : "todolist"} ${isOverForItemDrop || isOverDroppable ? "drop-target" : ""}`}
      onClick={onClick}
    >
      <div className="todo-info">
        <div className="todo-title">
          {todo.title}
          {linkedCount > 0 && (
            <span
              className="linked-indicator"
              title={t("lists.linkedTo", { count: linkedCount })}
            >
              <span className="material-symbols-outlined">link</span>
            </span>
          )}
        </div>
        <div className="todo-meta">
          <span
            className={`todo-type ${isChecklist ? "type-checklist" : "type-todolist"}`}
          >
            {isChecklist ? t("lists.checklist") : t("lists.todoList")}
          </span>
          :{" "}
          {(() => {
            // Spacers only separate items - don't count them
            const countableItems = todo.items.filter((item) => !item.isSpacer);
            const total = countableItems.length;
            const completed = countableItems.filter(
              (item) => item.completed,
            ).length;
            if (isChecklist) {
              return t("lists.checkedCount", { completed, total });
            } else {
              const uncompleted = total - completed;
              return t("lists.itemCount", { count: uncompleted });
            }
          })()}
        </div>
      </div>
      <button
        className="todo-edit-btn"
        onClick={onEdit}
        title={t("lists.editList")}
      >
        <span className="material-symbols-outlined">more_horiz</span>
      </button>
      <button
        className="todo-delete-btn"
        onClick={onDelete}
        title={t("lists.deleteList")}
      >
        <span className="material-symbols-outlined">delete</span>
      </button>
      <div
        className="drag-handle"
        {...attributes}
        {...listeners}
        style={{ touchAction: "none" }}
      >
        <span className="material-symbols-outlined">drag_indicator</span>
      </div>
    </div>
  );
}

// Sortable Item Component (right column)
function SortableToDoItem({
  item,
  color,
  onToggle,
  onDelete,
  onEditDescription,
  onUpdateDeadline,
  listId,
  modalRef,
  isDescriptionExpanded,
  onToggleDescriptionExpand,
  onEditSpacer,
  onMoveItem,
}) {
  const { t, i18n } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? "none" : transition,
    opacity: isDragging ? 0.5 : 1,
    borderLeftColor: color,
  };

  const deadlineStatus = !item.completed
    ? getDeadlineStatus(item.deadline)
    : null;

  const handleDeadlineClick = (e) => {
    e.stopPropagation();
    if (modalRef?.current) {
      modalRef.current.open({
        arrow: "auto-top-center",
        content: (
          <Deadline
            updateDeadline={(newDeadline) => {
              onUpdateDeadline(item._id, newDeadline);
              modalRef.current.close(true);
            }}
            selectedDeadline={item.deadline}
            reminderTarget={
              listId &&
              !item.isSpacer && { todoListId: listId, listItemId: item._id }
            }
          />
        ),
        triggerElement: e.currentTarget,
      });
    }
  };

  // A spacer's deadline applies to all items in its section
  const deadlineButton = (
    <button
      className={`list-item-deadline-btn ${item.deadline ? "has-deadline" : ""}`}
      onClick={handleDeadlineClick}
      title={
        item.deadline
          ? t("lists.deadlineTooltip", {
              date: new Date(item.deadline).toLocaleDateString(i18n.language),
            })
          : item.isSpacer
            ? t("lists.setSpacerDeadline")
            : t("lists.setDeadline")
      }
    >
      <span className="material-symbols-outlined">calendar_month</span>
      {item.deadline && (
        <span className="deadline-date">
          {new Date(item.deadline).toLocaleDateString(i18n.language, {
            day: "numeric",
            month: "short",
          })}
        </span>
      )}
    </button>
  );

  // Spacer: a titled, colored separator - can be edited, deleted and dragged,
  // but not checked off
  if (item.isSpacer) {
    const spacerColor = item.color || color;
    return (
      <div
        ref={setNodeRef}
        style={{
          ...style,
          borderLeftColor: spacerColor,
          backgroundColor: withAlpha(spacerColor, 0.1),
        }}
        className={`list-item list-item-spacer ${deadlineStatus || ""}`}
        onClick={(e) => {
          e.stopPropagation();
          onEditSpacer(item);
        }}
      >
        <div className="list-item-text-wrapper">
          <span className="list-item-text">{item.text}</span>
        </div>
        {deadlineButton}
        <button
          className="list-item-move-btn"
          onClick={(e) => {
            e.stopPropagation();
            onMoveItem(item);
          }}
          title={t("lists.moveTo")}
        >
          <span className="material-symbols-outlined">drive_file_move</span>
        </button>
        <button
          className="list-item-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(e, item);
          }}
          title={t("lists.deleteSpacer")}
        >
          <span className="material-symbols-outlined">delete</span>
        </button>
        <div
          className="drag-handle"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          style={{ touchAction: "none" }}
        >
          <span className="material-symbols-outlined">drag_indicator</span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`list-item ${item.completed ? "completed" : ""} ${deadlineStatus || ""}`}
      onClick={(e) => {
        e.stopPropagation();
        onEditDescription(item);
      }}
    >
      <input
        type="checkbox"
        checked={item.completed}
        onChange={() => onToggle(item._id)}
        onClick={(e) => e.stopPropagation()}
        className="list-item-checkbox"
      />
      <div className="list-item-text-wrapper">
        <span className="list-item-text">{item.text}</span>
        {item.description && item.description !== "<p></p>" && (
          <div
            className={`list-item-description ${isDescriptionExpanded ? "expanded" : ""}`}
            dangerouslySetInnerHTML={{
              __html: isDescriptionExpanded
                ? item.description
                : getTextPreview(item.description, 150),
            }}
          />
        )}
      </div>
      {item.description && item.description !== "<p></p>" && (
        <button
          className="list-item-chevron-btn"
          onClick={(e) => {
            e.stopPropagation();
            onToggleDescriptionExpand(item._id);
          }}
          title={
            isDescriptionExpanded ? t("lists.showLess") : t("lists.showMore")
          }
        >
          <span className="material-symbols-outlined">
            {isDescriptionExpanded ? "expand_more" : "chevron_left"}
          </span>
        </button>
      )}
      {deadlineButton}
      <button
        className="list-item-move-btn"
        onClick={(e) => {
          e.stopPropagation();
          onMoveItem(item);
        }}
        title={t("lists.moveTo")}
      >
        <span className="material-symbols-outlined">drive_file_move</span>
      </button>
      <button
        className="list-item-delete-btn"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(e, item);
        }}
        title={t("lists.deleteItem")}
      >
        <span className="material-symbols-outlined">delete</span>
      </button>
      <div
        className="drag-handle"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        style={{ touchAction: "none" }}
      >
        <span className="material-symbols-outlined">drag_indicator</span>
      </div>
    </div>
  );
}

function ToDoList({ scope = "personal", projectId = null }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState("");
  const [todos, setTodos] = useState([]);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [newItemText, setNewItemText] = useState("");
  const [showCompleted, setShowCompleted] = useState(() => {
    const saved = localStorage.getItem("todolist-showCompleted");
    return saved ? JSON.parse(saved) : false;
  });
  const [addToTop, setAddToTop] = useState(null); // null = use default, true = top, false = bottom
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const [linkedListCounts, setLinkedListCounts] = useState({});
  const [activeItem, setActiveItem] = useState(null); // Track item being dragged
  const [dragOverListId, setDragOverListId] = useState(null); // Track which list is being dragged over
  const searchInputRef = useRef(null);
  const newItemInputRef = useRef(null);
  const modalRef = useRef(null);
  const toolTipRef = useRef(null);
  const editorScrollableRef = useRef(null);
  // Latest lists, for answers given after a save (see askKeepItemDeadline)
  const todosRef = useRef(todos);

  useEffect(() => {
    todosRef.current = todos;
  }, [todos]);

  // Define fetchTodoLists first so it can be used in effects
  const fetchTodoLists = useCallback(async () => {
    try {
      let data;
      if (scope === "project" && projectId) {
        data = await getProjectTodoLists(projectId);
      } else {
        data = await getPersonalTodoLists();
      }
      // Sort by sortOrder to maintain drag-drop order
      const sortedData = [...data].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
      );
      setTodos(sortedData);
    } catch (error) {
      console.error("Failed to fetch todo lists:", error);
    }
  }, [scope, projectId]);

  // Fetch linked counts for project-scoped lists
  const fetchLinkedCounts = useCallback(async () => {
    if (scope === "project" && projectId) {
      try {
        const data = await getLinkedCounts(projectId);
        setLinkedListCounts(data.listCounts || {});
      } catch (error) {
        console.error("Error fetching linked counts:", error);
      }
    } else {
      setLinkedListCounts({});
    }
  }, [scope, projectId]);

  // Fetch todo lists on mount and when scope/projectId changes
  useEffect(() => {
    fetchTodoLists();
    fetchLinkedCounts();
    // Clear selection when scope changes
    setSelectedTodo(null);
  }, [fetchTodoLists, fetchLinkedCounts]);

  // Real-time WebSocket updates for todolists
  useUserRealtime(user?._id || user?.id, {
    onTodoListCreated: () => {
      fetchTodoLists();
    },
    onTodoListUpdated: () => {
      fetchTodoLists();
    },
    onTodoListDeleted: () => {
      fetchTodoLists();
    },
    onTodoListReordered: () => {
      fetchTodoLists();
    },
  });

  // Reload when this tab may have missed changes made on another device
  useResync(() => {
    fetchTodoLists();
    fetchLinkedCounts();
  });

  // Restore selected todo from localStorage after todos are loaded
  useEffect(() => {
    if (todos.length > 0 && !selectedTodo) {
      // Use scope-specific localStorage key
      const storageKey =
        scope === "project" && projectId
          ? `selectedTodoListId-project-${projectId}`
          : "selectedTodoListId-personal";
      const savedTodoId = localStorage.getItem(storageKey);
      if (savedTodoId) {
        const savedTodo = todos.find((todo) => todo._id === savedTodoId);
        if (savedTodo) {
          setSelectedTodo(savedTodo);
        }
      }
    }
  }, [todos, selectedTodo, scope, projectId]);

  // Handle selectList URL parameter from SuperSearch
  useEffect(() => {
    const selectListId = searchParams.get("selectList");
    const searchParam = searchParams.get("search");

    if (searchParam) {
      setSearchText(searchParam);
    }

    if (selectListId && todos.length > 0) {
      const listToSelect = todos.find((todo) => todo._id === selectListId);
      if (listToSelect) {
        setSelectedTodo(listToSelect);
      }
    }

    // Clear the URL parameters after reading them
    if (selectListId || searchParam) {
      setTimeout(() => {
        setSearchParams({});
      }, 200);
    }
  }, [searchParams, setSearchParams, todos]);

  // Load addToTop preference when selectedTodo changes
  useEffect(() => {
    if (selectedTodo) {
      const storageKey = `todolist-addToTop-${selectedTodo._id}`;
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) {
        setAddToTop(JSON.parse(saved));
      } else {
        // Set default based on list type: todolist = top, checklist = bottom
        setAddToTop(selectedTodo.type !== "checklist");
      }
    }
    // Only re-run when selectedTodo._id or type changes, not on every selectedTodo reference change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTodo?._id, selectedTodo?.type]);

  // Update selected todo when todos array changes (for real-time updates)
  useEffect(() => {
    if (selectedTodo && todos.length > 0) {
      const updatedTodo = todos.find((todo) => todo._id === selectedTodo._id);
      if (updatedTodo) {
        // Only update if the content has actually changed to avoid unnecessary re-renders
        if (JSON.stringify(updatedTodo) !== JSON.stringify(selectedTodo)) {
          setSelectedTodo(updatedTodo);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todos]);

  // Custom modifier to restrict dragging to vertical axis only
  const restrictToVerticalAxis = ({ transform }) => {
    return {
      ...transform,
      x: 0, // Force x to always be 0
    };
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  const openModal = (e, arrow, content) => {
    const triggerElement = e.currentTarget;

    if (modalRef.current) {
      modalRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const closeModal = () => {
    if (modalRef.current) {
      modalRef.current.close();
    }
  };

  const handleAddTodo = (e) => {
    const triggerElement = e.currentTarget;

    if (modalRef.current) {
      modalRef.current.open({
        arrow: "top-left",
        content: (
          <AddTodoList
            createTodoList={handleCreateTodoList}
            cancelCreateTodoList={closeModal}
          />
        ),
        triggerElement,
      });
    }
  };

  const handleCreateTodoList = async (title, color, listType = "todo") => {
    try {
      await createTodoList({
        title,
        color,
        type: listType,
        scope,
        projectId: scope === "project" ? projectId : null,
      });
      await fetchTodoLists();
      closeModal();
    } catch (error) {
      console.error("Failed to create todo list:", error);
    }
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
  };

  const getFilteredTodos = () => {
    if (!searchText.trim()) {
      return todos;
    }

    const searchLower = searchText.toLowerCase();

    return todos.filter((todo) => {
      // Check if title matches
      const titleMatches = todo.title.toLowerCase().includes(searchLower);

      // Check if any item text or description matches
      const itemMatches = todo.items?.some((item) => {
        const textMatches = item.text.toLowerCase().includes(searchLower);
        const descriptionMatches = item.description
          ? item.description.toLowerCase().includes(searchLower)
          : false;
        return textMatches || descriptionMatches;
      });

      return titleMatches || itemMatches;
    });
  };

  const handleTodoClick = (todo) => {
    setSelectedTodo(todo);
    // Use scope-specific localStorage key
    const storageKey =
      scope === "project" && projectId
        ? `selectedTodoListId-project-${projectId}`
        : "selectedTodoListId-personal";
    localStorage.setItem(storageKey, todo._id);
    // Focus the input field after selecting a list
    setTimeout(() => {
      newItemInputRef.current?.focus();
    }, 0);
  };

  const handleDeleteTodo = (e, todo) => {
    e.stopPropagation(); // Prevent selecting the todo when clicking delete

    openModal(
      e,
      "center-center",
      <RemoveTodoList
        removeTodoList={() => confirmRemoveTodoList(todo)}
        cancelRemoveTodoList={closeModal}
        todoListTitle={todo.title}
        itemCount={(todo.items || []).filter((item) => !item.isSpacer).length}
      />,
    );
  };

  const handleEditTodoList = (e, todo) => {
    e.stopPropagation(); // Prevent selecting the todo when clicking edit

    const triggerElement = e.currentTarget;

    if (modalRef.current) {
      modalRef.current.open({
        arrow: "center-center",
        content: (
          <EditTodoList
            todoListData={todo}
            updateTodoList={confirmUpdateTodoList}
            cancelEditTodoList={closeModal}
          />
        ),
        triggerElement,
      });
    }
  };

  const confirmUpdateTodoList = async (todoId, title, color, listType) => {
    try {
      const updatedTodo = await updateTodoList(todoId, {
        rev: todos.find((t) => t._id === todoId)?.rev,
        title,
        color,
        type: listType,
      });
      setTodos((prevTodos) =>
        prevTodos.map((t) => (t._id === todoId ? updatedTodo : t)),
      );

      // Update selected todo if it was the one being edited
      if (selectedTodo?._id === todoId) {
        setSelectedTodo(updatedTodo);
      }

      closeModal();
    } catch (error) {
      console.error("Failed to update todo list:", error);
      closeModal();
    }
  };

  const confirmRemoveTodoList = async (todo) => {
    try {
      await deleteTodoList(todo._id);
      setTodos((prevTodos) => prevTodos.filter((t) => t._id !== todo._id));

      // Clear selection if the deleted todo was selected
      if (selectedTodo?._id === todo._id) {
        setSelectedTodo(null);
        // Use scope-specific localStorage key
        const storageKey =
          scope === "project" && projectId
            ? `selectedTodoListId-project-${projectId}`
            : "selectedTodoListId-personal";
        localStorage.removeItem(storageKey);
      }

      closeModal();
    } catch (error) {
      console.error("Failed to delete todo list:", error);
      closeModal();
    }
  };

  const handleToggleAddPosition = (toTop) => {
    if (!selectedTodo) return;
    setAddToTop(toTop);
    const storageKey = `todolist-addToTop-${selectedTodo._id}`;
    localStorage.setItem(storageKey, JSON.stringify(toTop));
  };

  const showToolTip = (e, content) => {
    const triggerElement = e.currentTarget;
    if (toolTipRef.current) {
      toolTipRef.current.open({
        arrow: "top-right",
        content,
        triggerElement,
      });
    }
  };

  // Items of the selected list with new items added to the top or bottom.
  // The new items are added as one block, so they keep their own order
  // either way (e.g. a pasted list isn't reversed by add-to-top).
  const withNewItems = (newItems) => {
    const items = addToTop
      ? [...newItems, ...selectedTodo.items]
      : [...selectedTodo.items, ...newItems];
    return items.map((item, index) => ({ ...item, sortOrder: index }));
  };

  const withNewItem = (newItem) => withNewItems([newItem]);

  // Add items with the given texts (in this order) to the selected list
  const addItems = async (texts) => {
    if (!selectedTodo || texts.length === 0) return false;

    // New items under a spacer with a deadline get its deadline
    const firstNewIndex = addToTop ? 0 : selectedTodo.items.length;
    const updatedItems = applyNewItemDeadline(
      withNewItems(texts.map((text) => ({ text, completed: false }))),
      texts.map((_, index) => firstNewIndex + index),
    );

    try {
      const updatedTodo = await updateTodoList(selectedTodo._id, {
        rev: selectedTodo.rev,
        items: updatedItems,
      });

      setTodos((prevTodos) =>
        prevTodos.map((todo) =>
          todo._id === selectedTodo._id ? updatedTodo : todo,
        ),
      );

      setSelectedTodo(updatedTodo);
      return true;
    } catch (error) {
      console.error("Failed to add item:", error);
      return false;
    }
  };

  const handleAddItem = async () => {
    if (!newItemText.trim()) return;
    if (await addItems([newItemText.trim()])) {
      setNewItemText("");
    }
  };

  // Pasting a list (bullets from Google Docs, a website, lines of text...)
  // adds each entry as its own item
  const handleNewItemPaste = (e) => {
    const texts = parsePastedList(e.clipboardData);
    if (!texts) return; // A single line - paste normally
    e.preventDefault();
    addItems(texts);
  };

  // Save a spacer: add a new one, or update the given one's title and color
  const saveSpacer = async (spacer, title, color) => {
    if (!selectedTodo) return;

    const updatedItems = spacer
      ? selectedTodo.items.map((item) =>
          item._id === spacer._id ? { ...item, text: title, color } : item,
        )
      : withNewItem({ text: title, color, isSpacer: true, completed: false });

    try {
      const updatedTodo = await updateTodoList(selectedTodo._id, {
        rev: selectedTodo.rev,
        items: updatedItems,
      });

      setTodos((prevTodos) =>
        prevTodos.map((todo) =>
          todo._id === selectedTodo._id ? updatedTodo : todo,
        ),
      );

      setSelectedTodo(updatedTodo);
    } catch (error) {
      console.error("Failed to save spacer:", error);
    }
  };

  // Open the spacer modal - pass a spacer to edit it, or nothing to add a new one
  const handleOpenSpacerModal = (spacer = null) => {
    if (!modalRef.current || !selectedTodo) return;
    modalRef.current.open({
      arrow: "center-center",
      content: (
        <SpacerModal
          spacer={spacer}
          defaultColor={selectedTodo.color}
          onSave={(title, color) => {
            saveSpacer(spacer, title, color);
            closeModal();
          }}
          onCancel={closeModal}
        />
      ),
      triggerElement: null, // No trigger for center-center modals
    });
  };

  // Move an item to the top of another list, or into a spacer's section
  // (in this or another list - see insertUnderSpacer)
  const moveItem = async (item, targetList, spacer) => {
    if (!selectedTodo) return;

    try {
      const updatedSourceItems = selectedTodo.items
        .filter((i) => i._id !== item._id)
        .map((i, index) => ({ ...i, sortOrder: index }));

      if (targetList._id === selectedTodo._id) {
        // Moving within this list - a single update. The item gets the
        // deadline of its new spacer (or we ask whether to keep its own).
        const { items, askKeepRemove } = applyMoveDeadlines(
          selectedTodo.items,
          insertUnderSpacer(
            updatedSourceItems,
            spacer._id,
            item,
            getAddToTop(selectedTodo),
          ),
          item._id,
        );
        await updateTodoList(selectedTodo._id, {
          rev: selectedTodo.rev,
          items,
        });
        if (askKeepRemove) {
          await fetchTodoLists();
          askKeepItemDeadline(selectedTodo._id, item._id);
          return;
        }
      } else {
        const updatedTargetItems = spacer
          ? insertUnderSpacer(
              targetList.items,
              spacer._id,
              item,
              getAddToTop(targetList),
            )
          : [
              { ...item, sortOrder: 0 },
              ...targetList.items.map((i, index) => ({
                ...i,
                sortOrder: index + 1,
              })),
            ];

        // Target first, so a rejected (stale) update can never remove the
        // item from the source without adding it to the target
        await updateTodoList(targetList._id, {
          rev: targetList.rev,
          items: updatedTargetItems,
        });
        await updateTodoList(selectedTodo._id, {
          rev: selectedTodo.rev,
          items: updatedSourceItems,
        });
      }
    } catch (error) {
      console.error("Failed to move item:", error);
    }
    // Refetch to ensure consistency (also after a failed/partial move)
    await fetchTodoLists();
  };

  const handleOpenMoveItemModal = (item) => {
    if (!modalRef.current || !selectedTodo) return;
    modalRef.current.open({
      arrow: "center-center",
      content: (
        <MoveItemModal
          item={item}
          currentList={selectedTodo}
          lists={todos}
          onMove={(targetList, spacer) => {
            closeModal();
            moveItem(item, targetList, spacer);
          }}
          onCancel={closeModal}
        />
      ),
      triggerElement: null, // No trigger for center-center modals
    });
  };

  const handleToggleItem = async (itemId) => {
    if (!selectedTodo) return;

    const updatedItems = selectedTodo.items.map((item) =>
      item._id === itemId ? { ...item, completed: !item.completed } : item,
    );

    try {
      const updatedTodo = await updateTodoList(selectedTodo._id, {
        rev: selectedTodo.rev,
        items: updatedItems,
      });

      setTodos((prevTodos) =>
        prevTodos.map((todo) =>
          todo._id === selectedTodo._id ? updatedTodo : todo,
        ),
      );

      setSelectedTodo(updatedTodo);
    } catch (error) {
      console.error("Failed to toggle item:", error);
    }
  };

  // After an item with a deadline was moved out of a spacer section with a
  // deadline into one without: ask whether to keep or remove its deadline
  // (dismissing keeps it)
  const askKeepItemDeadline = (listId, itemId) => {
    const item = todosRef.current
      .find((todo) => todo._id === listId)
      ?.items.find((i) => i._id === itemId);
    if (!modalRef.current || !item?.deadline) return;
    modalRef.current.open({
      arrow: "center-center",
      content: (
        <KeepItemDeadline
          item={item}
          onKeep={closeModal}
          onRemove={() => {
            closeModal();
            removeMovedItemDeadline(listId, itemId);
          }}
        />
      ),
      triggerElement: null, // No trigger for center-center modals
    });
  };

  const removeMovedItemDeadline = async (listId, itemId) => {
    // The list as it is now (the move has been saved since the question)
    const list = todosRef.current.find((todo) => todo._id === listId);
    if (!list) return;

    try {
      const updatedTodo = await updateTodoList(listId, {
        rev: list.rev,
        items: removeItemDeadline(list.items, itemId),
      });

      setTodos((prevTodos) =>
        prevTodos.map((todo) => (todo._id === listId ? updatedTodo : todo)),
      );
      setSelectedTodo((prev) => (prev?._id === listId ? updatedTodo : prev));
    } catch (error) {
      console.error("Failed to remove item deadline:", error);
    }
  };

  const handleUpdateItemDeadline = async (itemId, newDeadline) => {
    if (!selectedTodo) return;

    // A spacer's deadline is set on all items in its section
    const isSpacer = selectedTodo.items.some(
      (item) => item._id === itemId && item.isSpacer,
    );
    const updatedItems = isSpacer
      ? applySpacerDeadline(selectedTodo.items, itemId, newDeadline)
      : selectedTodo.items.map((item) =>
          item._id === itemId ? { ...item, deadline: newDeadline } : item,
        );

    try {
      const updatedTodo = await updateTodoList(selectedTodo._id, {
        rev: selectedTodo.rev,
        items: updatedItems,
      });

      setTodos((prevTodos) =>
        prevTodos.map((todo) =>
          todo._id === selectedTodo._id ? updatedTodo : todo,
        ),
      );

      setSelectedTodo(updatedTodo);
    } catch (error) {
      console.error("Failed to update item deadline:", error);
    }
  };

  const handleEditItemDescription = (item) => {
    // Open modal with modalType to prevent other description modals from closing
    if (modalRef.current) {
      modalRef.current.open({
        arrow: "center-center",
        content: (
          <EditItemDescription
            item={item}
            todoListId={selectedTodo?._id}
            color={selectedTodo?.color}
            onUpdate={(itemId, title, description) =>
              handleUpdateItemDescription(itemId, title, description)
            }
            onCancel={handleCancelEditItemDescription}
          />
        ),
        triggerElement: null, // No trigger for center-center modals
        modalType: "description",
      });
    }
  };

  const handleUpdateItemDescription = async (itemId, title, description) => {
    if (!selectedTodo) {
      console.error("No todo list selected");
      return;
    }

    const updatedItems = selectedTodo.items.map((item) =>
      item._id === itemId ? { ...item, text: title, description } : item,
    );

    try {
      const updatedTodo = await updateTodoList(selectedTodo._id, {
        rev: selectedTodo.rev,
        items: updatedItems,
      });

      setTodos((prevTodos) =>
        prevTodos.map((todo) =>
          todo._id === selectedTodo._id ? updatedTodo : todo,
        ),
      );

      setSelectedTodo(updatedTodo);

      // Close modal after successful update
      if (modalRef.current) {
        modalRef.current.close(true);
      }
    } catch (error) {
      console.error("Failed to update item description:", error);
    }
  };

  const handleCancelEditItemDescription = () => {
    if (modalRef.current) {
      modalRef.current.close(true);
    }
  };

  // Helper function to convert HTML to formatted plain text with line breaks and bullets
  const htmlToFormattedText = (html) => {
    if (!html) return "";

    let text = html;

    // Replace list items with bullet points
    text = text.replace(/<li>/gi, "• ");
    text = text.replace(/<\/li>/gi, "\n");

    // Replace paragraph and div tags with line breaks
    text = text.replace(/<\/p>/gi, "\n");
    text = text.replace(/<\/div>/gi, "\n");
    text = text.replace(/<br\s*\/?>/gi, "\n");

    // Remove opening tags
    text = text.replace(/<p>/gi, "");
    text = text.replace(/<div>/gi, "");
    text = text.replace(/<ul>/gi, "");
    text = text.replace(/<\/ul>/gi, "");
    text = text.replace(/<ol>/gi, "");
    text = text.replace(/<\/ol>/gi, "");

    // Remove any remaining HTML tags
    text = text.replace(/<[^>]*>/g, "");

    // Decode HTML entities
    const tmp = document.createElement("div");
    tmp.innerHTML = text;
    text = tmp.textContent || tmp.innerText || "";

    // Clean up extra whitespace while preserving intentional line breaks
    text = text.replace(/\n\s*\n\s*\n/g, "\n\n"); // Max 2 consecutive line breaks
    text = text.trim();

    return text;
  };

  // Generate PDF for the selected todo list
  const generatePDF = async (includeDescriptions = false) => {
    if (!selectedTodo) return;

    const doc = new jsPDF();

    // Load Exo 2 font
    await loadExo2Font(doc);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let yPosition = margin;

    // Title
    const listType =
      selectedTodo.type === "checklist"
        ? t("lists.checklist")
        : t("lists.todoList");
    doc.setFontSize(18);
    doc.setFont("Exo2", "bold");
    doc.text(selectedTodo.title, margin, yPosition);
    yPosition += 8;

    // Subtitle with type and date
    doc.setFontSize(10);
    doc.setFont("Exo2", "normal");
    doc.setTextColor(128, 128, 128);
    doc.text(
      t("lists.pdfSubtitle", {
        type: listType,
        date: new Date().toLocaleDateString(i18n.language),
      }),
      margin,
      yPosition,
    );
    yPosition += 15;

    // Reset text color
    doc.setTextColor(0, 0, 0);

    // Determine which items to include based on list type
    const isChecklist = selectedTodo.type === "checklist";
    const itemsToInclude = isChecklist
      ? selectedTodo.items // Checklists: include all items
      : selectedTodo.items.filter((item) => !item.completed); // To-do lists: only uncompleted items

    // Items
    itemsToInclude.forEach((item) => {
      // Check if we need a new page
      if (yPosition > pageHeight - 30) {
        doc.addPage();
        yPosition = margin;
      }

      // Spacers are printed as small headings without a checkbox
      if (item.isSpacer) {
        yPosition += 3;
        doc.setFontSize(13);
        doc.setFont("Exo2", "bold");
        doc.setTextColor(0, 0, 0);
        doc.splitTextToSize(item.text, maxWidth).forEach((line) => {
          doc.text(line, margin, yPosition);
          yPosition += 6;
        });
        yPosition += 3;
        return;
      }

      // Draw checkbox - always unchecked in PDF (checklists show all unchecked, todos only show uncompleted)
      doc.setFontSize(12);
      doc.setFont("Exo2", "normal");
      doc.setTextColor(0, 0, 0);
      doc.setDrawColor(0, 0, 0);

      // Draw checkbox rectangle
      const checkboxSize = 4;
      const checkboxY = yPosition - checkboxSize + 1;
      doc.rect(margin, checkboxY, checkboxSize, checkboxSize);

      // Item text
      const textX = margin + checkboxSize + 4;
      const textMaxWidth = maxWidth - checkboxSize - 4;
      const lines = doc.splitTextToSize(item.text, textMaxWidth);

      lines.forEach((line) => {
        if (yPosition > pageHeight - 20) {
          doc.addPage();
          yPosition = margin;
        }
        doc.text(line, textX, yPosition);
        yPosition += 6;
      });

      // Add description if included and exists
      if (
        includeDescriptions &&
        item.description &&
        item.description !== "<p></p>"
      ) {
        const descriptionText = htmlToFormattedText(item.description);
        if (descriptionText.trim()) {
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);

          // Split by line breaks first, then wrap each line
          const paragraphs = descriptionText.split("\n");
          paragraphs.forEach((paragraph) => {
            if (paragraph.trim()) {
              const descLines = doc.splitTextToSize(
                paragraph,
                textMaxWidth - 6,
              );
              descLines.forEach((line) => {
                if (yPosition > pageHeight - 20) {
                  doc.addPage();
                  yPosition = margin;
                }
                doc.text(line, textX + 2, yPosition);
                yPosition += 5;
              });
            } else {
              // Empty line for paragraph spacing
              yPosition += 2;
            }
          });

          yPosition += 3;
        }
      }

      yPosition += 4;
    });

    // Save the PDF - preserve Danish characters (æ, ø, å) and other unicode
    const filename = `${selectedTodo.title.replace(/[<>:"/\\|?*]/g, "_")}.pdf`;
    doc.save(filename);
  };

  const handleOpenDownloadModal = (e) => {
    if (modalRef.current && selectedTodo) {
      modalRef.current.open({
        arrow: "top-center",
        content: (
          <DownloadList
            onDownloadPlain={async () => {
              await generatePDF(false);
              modalRef.current.close(true);
            }}
            onDownloadWithDescriptions={async () => {
              await generatePDF(true);
              modalRef.current.close(true);
            }}
            onCancel={() => modalRef.current.close(true)}
          />
        ),
        triggerElement: e.currentTarget,
      });
    }
  };

  const handleToggleDescriptionExpand = (itemId) => {
    setExpandedDescriptions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleRemoveCompleted = (e) => {
    console.log("handleRemoveCompleted called", e, selectedTodo);
    if (!selectedTodo) return;

    const completedCount = selectedTodo.items.filter(
      (item) => item.completed,
    ).length;

    console.log("Completed count:", completedCount);
    console.log("Modal ref:", modalRef.current);

    const triggerElement = e.currentTarget;

    if (modalRef.current) {
      modalRef.current.open({
        arrow: "auto-top-center",
        content: (
          <RemoveCompletedItems
            cancelRemoveCompletedItems={cancelRemoveCompletedItems}
            removeCompletedItems={confirmRemoveCompletedItems}
            completedCount={completedCount}
          />
        ),
        triggerElement,
      });
    }
  };

  const cancelRemoveCompletedItems = () => {
    modalRef.current.close();
  };

  const confirmRemoveCompletedItems = async () => {
    if (!selectedTodo) return;

    // Save scroll position before update
    const scrollContainer = editorScrollableRef.current;
    const savedScrollTop = scrollContainer?.scrollTop || 0;

    try {
      // Filter out completed items and update sortOrder
      const updatedItems = selectedTodo.items
        .filter((item) => !item.completed)
        .map((item, index) => ({
          ...item,
          sortOrder: index,
        }));

      await updateTodoList(selectedTodo._id, {
        rev: selectedTodo.rev,
        items: updatedItems,
      });

      // Update local state
      setSelectedTodo({
        ...selectedTodo,
        items: updatedItems,
      });

      setTodos(
        todos.map((todo) =>
          todo._id === selectedTodo._id
            ? { ...todo, items: updatedItems }
            : todo,
        ),
      );

      modalRef.current.close();

      // Restore scroll position after React updates
      requestAnimationFrame(() => {
        if (scrollContainer) {
          scrollContainer.scrollTop = savedScrollTop;
        }
      });
    } catch (error) {
      console.error("Failed to remove completed items:", error);
    }
  };

  const handleUncheckAll = async () => {
    if (!selectedTodo) return;

    try {
      // Set all items to unchecked (completed: false)
      const updatedItems = selectedTodo.items.map((item) => ({
        ...item,
        completed: false,
      }));

      const updatedTodo = await updateTodoList(selectedTodo._id, {
        rev: selectedTodo.rev,
        items: updatedItems,
      });

      setTodos((prevTodos) =>
        prevTodos.map((todo) =>
          todo._id === selectedTodo._id ? updatedTodo : todo,
        ),
      );

      setSelectedTodo(updatedTodo);
    } catch (error) {
      console.error("Failed to uncheck all items:", error);
    }
  };

  const handleDeleteItem = (e, item) => {
    if (!selectedTodo) return;

    const triggerElement = e.currentTarget;

    if (modalRef.current) {
      modalRef.current.open({
        arrow: "center-center",
        content: (
          <div className="delete-item-modal">
            <div className="modal-title">
              {t("lists.modals.deleteItem.title")}
            </div>
            <div className="modal-form">
              <p style={{ marginTop: "10px", marginBottom: "0" }}>
                {t("lists.modals.deleteConfirm", { name: item.text })}
              </p>
              <div className="modal-form-buttons">
                <button
                  className="modal-button cancel"
                  onClick={() => modalRef.current.close()}
                >
                  {t("common.cancel")}
                </button>
                <button
                  className="modal-button delete"
                  onClick={() => confirmDeleteItem(item._id)}
                >
                  {t("common.delete")}
                </button>
              </div>
            </div>
          </div>
        ),
        triggerElement,
      });
    }
  };

  const confirmDeleteItem = async (itemId) => {
    if (!selectedTodo) return;

    // Save scroll position before update
    const scrollContainer = editorScrollableRef.current;
    const savedScrollTop = scrollContainer?.scrollTop || 0;

    try {
      // Remove the item from the list
      const updatedItems = selectedTodo.items
        .filter((item) => item._id !== itemId)
        .map((item, index) => ({
          ...item,
          sortOrder: index,
        }));

      const updatedTodo = await updateTodoList(selectedTodo._id, {
        rev: selectedTodo.rev,
        items: updatedItems,
      });

      setTodos((prevTodos) =>
        prevTodos.map((todo) =>
          todo._id === selectedTodo._id ? updatedTodo : todo,
        ),
      );

      setSelectedTodo(updatedTodo);
      modalRef.current.close();

      // Restore scroll position after React updates
      requestAnimationFrame(() => {
        if (scrollContainer) {
          scrollContainer.scrollTop = savedScrollTop;
        }
      });
    } catch (error) {
      console.error("Failed to delete item:", error);
    }
  };

  const handleUnifiedDragStart = (event) => {
    const { active } = event;

    // Check if dragging an item (not a list)
    if (selectedTodo) {
      const item = selectedTodo.items.find((item) => item._id === active.id);
      if (item) {
        setActiveItem(item);
      }
    }
  };

  const handleUnifiedDragOver = (event) => {
    const { over } = event;
    if (!over) {
      setDragOverListId(null);
      return;
    }

    // Only highlight when dragging an item (not a list)
    if (!activeItem) {
      setDragOverListId(null);
      return;
    }

    // Check if dragging over a list drop zone
    if (over.id.toString().startsWith("list-drop-")) {
      const listId = over.id.toString().replace("list-drop-", "");
      setDragOverListId(listId);
    } else if (todos.some((todo) => todo._id === over.id)) {
      // Also handle dragging over a list's sortable ID
      setDragOverListId(over.id);
    } else {
      setDragOverListId(null);
    }
  };

  const handleUnifiedDragEnd = async (event) => {
    const { active, over } = event;

    // Clear drag state
    setActiveItem(null);
    setDragOverListId(null);

    if (!over || active.id === over.id) {
      return;
    }

    // Check if we're dragging a list (for list reordering)
    const isDraggingList = todos.some((todo) => todo._id === active.id);

    // Check if we're dragging an item
    const isDraggingItem = selectedTodo?.items?.some(
      (item) => item._id === active.id,
    );

    // Determine target list ID (could be list-drop-{id} or just {id})
    let targetListId = null;
    if (over.id.toString().startsWith("list-drop-")) {
      targetListId = over.id.toString().replace("list-drop-", "");
    } else if (todos.some((todo) => todo._id === over.id)) {
      targetListId = over.id;
    }

    // If dragging an item onto a list, handle cross-list move
    if (isDraggingItem && targetListId && selectedTodo) {
      // Don't move if dropping on the same list
      if (targetListId === selectedTodo._id) {
        return;
      }

      const targetList = todos.find((t) => t._id === targetListId);
      if (!targetList) {
        return;
      }

      const movedItem = selectedTodo.items.find(
        (item) => item._id === active.id,
      );
      if (!movedItem) {
        return;
      }

      try {
        // Remove from source list
        const updatedSourceItems = selectedTodo.items
          .filter((item) => item._id !== active.id)
          .map((item, index) => ({
            ...item,
            sortOrder: index,
          }));

        // Add to target list
        const updatedTargetItems = [
          ...targetList.items,
          {
            ...movedItem,
            sortOrder: targetList.items.length,
          },
        ];

        // Update both lists - target first, so a rejected (stale) update
        // can never remove the item from the source without adding it to the target
        await updateTodoList(targetListId, {
          rev: targetList.rev,
          items: updatedTargetItems,
        });

        await updateTodoList(selectedTodo._id, {
          rev: selectedTodo.rev,
          items: updatedSourceItems,
        });

        // Refetch to ensure consistency
        await fetchTodoLists();
      } catch (error) {
        console.error("Failed to move item between lists:", error);
        await fetchTodoLists();
      }

      return;
    }

    // If dragging a list, handle list reordering
    if (isDraggingList && !isDraggingItem) {
      const oldIndex = todos.findIndex((todo) => todo._id === active.id);
      const newIndex = todos.findIndex((todo) => todo._id === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      const newTodos = arrayMove(todos, oldIndex, newIndex).map(
        (todo, index) => ({
          ...todo,
          sortOrder: index,
        }),
      );

      // Optimistically update the UI
      setTodos(newTodos);

      // Build updates array for batch reorder
      const updates = newTodos.map((todo) => ({
        _id: todo._id,
        sortOrder: todo.sortOrder,
      }));

      // Save to API using batch endpoint and refetch
      try {
        await reorderTodoLists(updates);
        await fetchTodoLists();
      } catch (error) {
        console.error("Failed to reorder todo lists:", error);
        await fetchTodoLists();
      }

      return;
    }

    // Handle item reordering within the same list
    if (!selectedTodo || !isDraggingItem) {
      return;
    }

    // Otherwise, reorder within the same list
    const oldIndex = selectedTodo.items.findIndex(
      (item) => item._id === active.id,
    );
    const newIndex = selectedTodo.items.findIndex(
      (item) => item._id === over.id,
    );

    if (oldIndex === -1 || newIndex === -1) return;

    // Items moved into a spacer section with a deadline get it (see
    // applyMoveDeadlines - we may also have to ask about the item's deadline)
    const { items: newItems, askKeepRemove } = applyMoveDeadlines(
      selectedTodo.items,
      arrayMove(selectedTodo.items, oldIndex, newIndex).map((item, index) => ({
        ...item,
        sortOrder: index,
      })),
      active.id,
    );

    // Optimistically update the UI first
    const updatedTodoOptimistic = {
      ...selectedTodo,
      items: newItems,
    };

    setSelectedTodo(updatedTodoOptimistic);
    setTodos((prevTodos) =>
      prevTodos.map((todo) =>
        todo._id === selectedTodo._id ? updatedTodoOptimistic : todo,
      ),
    );

    // Then save to the API
    try {
      const updatedTodo = await updateTodoList(selectedTodo._id, {
        rev: selectedTodo.rev,
        items: newItems,
      });

      // Update with server response
      setTodos((prevTodos) =>
        prevTodos.map((todo) =>
          todo._id === selectedTodo._id ? updatedTodo : todo,
        ),
      );

      setSelectedTodo(updatedTodo);

      if (askKeepRemove) {
        // Ask once the move is saved (the answer is saved on top of it)
        askKeepItemDeadline(updatedTodo._id, active.id);
      }
    } catch (error) {
      console.error("Failed to reorder items:", error);
      // Optionally revert the optimistic update on error
    }
  };

  return (
    <div className="todolist-wrapper">
      <ToolTip ref={toolTipRef} />
      <ActionBar>
        <ActionBarButton
          buttonText={t("lists.actions.addList")}
          icon={
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px" }}
            >
              add
            </span>
          }
          onClick={handleAddTodo}
        />
        {selectedTodo && selectedTodo.type !== "checklist" && (
          <>
            <div className="action-bar-spacer" />
            <ActionBarButton
              buttonText={
                showCompleted
                  ? t("lists.actions.hideCompleted")
                  : t("lists.actions.showCompleted")
              }
              icon={
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "20px" }}
                >
                  {showCompleted ? "visibility_off" : "visibility"}
                </span>
              }
              onClick={() => {
                const newValue = !showCompleted;
                setShowCompleted(newValue);
                localStorage.setItem(
                  "todolist-showCompleted",
                  JSON.stringify(newValue),
                );
              }}
              isActive={showCompleted}
            />
          </>
        )}
        {selectedTodo && selectedTodo.items.some((item) => item.completed) && (
          <>
            {(selectedTodo.type === "checklist" || showCompleted) && (
              <div className="action-bar-spacer" />
            )}
            {selectedTodo.type === "checklist" ? (
              <ActionBarButton
                buttonText={t("lists.actions.uncheckAll")}
                textColor="#6b9bd1"
                icon={
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "20px", color: "#6b9bd1" }}
                  >
                    check_box_outline_blank
                  </span>
                }
                onClick={handleUncheckAll}
              />
            ) : (
              showCompleted && (
                <ActionBarButton
                  buttonText={t("lists.actions.removeCompleted")}
                  textColor="#e8697d"
                  icon={
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "20px", color: "#e8697d" }}
                    >
                      delete_sweep
                    </span>
                  }
                  onClick={handleRemoveCompleted}
                />
              )
            )}
          </>
        )}
        {selectedTodo && (
          <>
            <div className="action-bar-spacer" />
            <ActionBarButton
              buttonText={t("lists.actions.downloadPdf")}
              icon={
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "20px" }}
                >
                  download
                </span>
              }
              onClick={handleOpenDownloadModal}
            />
          </>
        )}
      </ActionBar>
      <div className="todolist">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleUnifiedDragStart}
          onDragOver={handleUnifiedDragOver}
          onDragEnd={handleUnifiedDragEnd}
        >
          <SortableContext
            items={[
              ...getFilteredTodos().map((todo) => todo._id),
              ...(selectedTodo?.items || []).map((item) => item._id),
            ]}
            strategy={verticalListSortingStrategy}
          >
            <div className="todolist-content">
              <div className="todolist-left-column">
                <div className="todolist-column-header">{t("lists.lists")}</div>
                <div className="todolist-search">
                  <FlowTextInput
                    ref={searchInputRef}
                    placeholder={t("lists.filterPlaceholder")}
                    value={searchText}
                    onChange={handleSearchChange}
                    expandable={false}
                    icon="search"
                  />
                </div>
                {getFilteredTodos().length === 0 ? (
                  <EmptyStatePlaceholder
                    topText={t("lists.empty.noLists")}
                    bottomText={
                      searchText.trim()
                        ? t("lists.empty.adjustSearch")
                        : t("lists.empty.createList")
                    }
                  />
                ) : (
                  <div className="todolist-list">
                    {getFilteredTodos().map((todo) => (
                      <SortableTodoListItem
                        key={todo._id}
                        todo={todo}
                        isSelected={selectedTodo?._id === todo._id}
                        onClick={() => handleTodoClick(todo)}
                        disabled={!!activeItem}
                        onEdit={(e) => handleEditTodoList(e, todo)}
                        onDelete={(e) => handleDeleteTodo(e, todo)}
                        linkedCount={linkedListCounts[todo._id] || 0}
                        isOverForItemDrop={dragOverListId === todo._id}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="todo-editor-column">
                <div className="todolist-column-header">
                  {selectedTodo ? (
                    <>
                      <span
                        className={`column-type-label ${selectedTodo.type === "checklist" ? "type-checklist" : "type-todolist"}`}
                      >
                        {selectedTodo.type === "checklist"
                          ? t("lists.checklist")
                          : t("lists.todoList")}
                      </span>
                      : {selectedTodo.title}
                    </>
                  ) : (
                    t("lists.listItems")
                  )}
                </div>
                {!selectedTodo ? (
                  <EmptyStatePlaceholder
                    topText={t("lists.empty.selectList")}
                    bottomText={t("lists.empty.selectListHint")}
                  />
                ) : (
                  <>
                    <div className="todo-editor-wrapper">
                      <div className="todo-add-item">
                        <FlowTextInput
                          ref={newItemInputRef}
                          placeholder={t("lists.addNewItem")}
                          value={newItemText}
                          onChange={setNewItemText}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleAddItem();
                            }
                          }}
                          onPaste={handleNewItemPaste}
                          expandable={false}
                          icon="add"
                        />
                        <button
                          className="todo-action-btn"
                          onClick={() => handleOpenSpacerModal()}
                          onMouseEnter={(e) =>
                            showToolTip(e, t("lists.addSpacerHint"))
                          }
                        >
                          <span className="material-symbols-outlined">
                            view_day
                          </span>
                        </button>
                        <button
                          className={`todo-action-btn ${addToTop ? "active" : ""}`}
                          onClick={() => handleToggleAddPosition(true)}
                          onMouseEnter={(e) =>
                            showToolTip(e, t("lists.addToTop"))
                          }
                        >
                          <span className="material-symbols-outlined">
                            vertical_align_top
                          </span>
                        </button>
                        <button
                          className={`todo-action-btn ${!addToTop ? "active" : ""}`}
                          onClick={() => handleToggleAddPosition(false)}
                          onMouseEnter={(e) =>
                            showToolTip(e, t("lists.addToBottom"))
                          }
                        >
                          <span className="material-symbols-outlined">
                            vertical_align_bottom
                          </span>
                        </button>
                      </div>
                      <div
                        ref={editorScrollableRef}
                        className="todo-editor-scrollable"
                        onClick={() => newItemInputRef.current?.focus()}
                      >
                        {selectedTodo.items && selectedTodo.items.length > 0 ? (
                          <>
                            <div className="todo-items-list">
                              {selectedTodo.items
                                .filter(
                                  (item) =>
                                    selectedTodo.type === "checklist" ||
                                    showCompleted ||
                                    !item.completed,
                                )
                                .map((item) => (
                                  <SortableToDoItem
                                    key={item._id}
                                    item={item}
                                    color={selectedTodo.color}
                                    onToggle={handleToggleItem}
                                    onDelete={handleDeleteItem}
                                    onEditDescription={
                                      handleEditItemDescription
                                    }
                                    onUpdateDeadline={handleUpdateItemDeadline}
                                    listId={selectedTodo._id}
                                    modalRef={modalRef}
                                    isDescriptionExpanded={expandedDescriptions.has(
                                      item._id,
                                    )}
                                    onToggleDescriptionExpand={
                                      handleToggleDescriptionExpand
                                    }
                                    onEditSpacer={handleOpenSpacerModal}
                                    onMoveItem={handleOpenMoveItemModal}
                                  />
                                ))}
                            </div>
                          </>
                        ) : (
                          <div className="todo-editor-content">
                            <p>{t("lists.empty.noItems")}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </SortableContext>
          <DragOverlay>
            {activeItem?.isSpacer ? (
              <div
                className="list-item list-item-spacer"
                style={{
                  cursor: "grabbing",
                  opacity: 0.9,
                  borderLeftColor: activeItem.color || selectedTodo?.color,
                  backgroundColor: withAlpha(
                    activeItem.color || selectedTodo?.color,
                    0.1,
                  ),
                }}
              >
                <div className="list-item-text-wrapper">
                  <span className="list-item-text">{activeItem.text}</span>
                </div>
              </div>
            ) : activeItem ? (
              <div
                className={`list-item ${activeItem.completed ? "completed" : ""}`}
                style={{
                  cursor: "grabbing",
                  opacity: 0.9,
                  borderLeftColor: selectedTodo?.color || "#2196f3",
                }}
              >
                <input
                  type="checkbox"
                  checked={activeItem.completed}
                  readOnly
                  className="list-item-checkbox"
                />
                <div className="list-item-text-wrapper">
                  <span className="list-item-text">{activeItem.text}</span>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
      <Modal ref={modalRef} />
    </div>
  );
}

export default ToDoList;
